/**
 * How stored values read on screen: dates, staff roles, audit actions, and the Worker's refusal
 * codes. Codes stay stable in the API and the wording lives here, so a rewording never touches the
 * Worker.
 */
import type {
  ClinicRole,
  DeclineReason,
  FunnelStage,
  PharmacyOrderState,
  IntakeAnswers,
  ReviewEditChange,
  ReviewEditReason,
  StaffRole,
} from "@lattice/core";

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" });

/* Audit times carry their zone, because staff in different zones compare them in tickets. */
const DATE_TIME = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

export function day(iso: string | null): string {
  if (iso === null) return "Not recorded";
  /* A bare calendar date such as a collection date is read as UTC midnight, then shown in UTC, so
     a reader west of Greenwich does not see the day before. */
  const date = new Date(iso);

  return /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" }).format(date)
    : DATE.format(date);
}

export function moment(iso: string): string {
  return DATE_TIME.format(new Date(iso));
}

/** Waiting time on the review queue, against the 24-hour target. */
export function waited(ms: number): string {
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);

  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;

  if (hours > 0) return `${hours}h ${minutes}m`;

  return `${minutes}m`;
}

export const STAFF_ROLE: Readonly<Record<StaffRole, string>> = {
  superuser: "Superuser",
  clinician: "Reviewing clinician",
  operations: "Operations",
  support: "Support",
  engine: "Engine",
};

export const CLINIC_ROLE: Readonly<Record<ClinicRole, string>> = {
  admin: "Clinic admin",
  practitioner: "Practitioner",
  assistant: "Assistant",
};

export const PHARMACY_STATE: Readonly<Record<PharmacyOrderState, string>> = {
  queued: "Queued",
  submitted: "Submitted",
  accepted: "Accepted",
  in_production: "In production",
  quality_check: "Quality check",
  shipped: "Shipped",
  delivered: "Delivered",
  rejected: "Rejected",
  on_hold: "On hold",
  delivery_exception: "Delivery exception",
  cancelled: "Cancelled",
};

export const ORDER_STATUS = {
  pending_payment: "Pending payment",
  paid: "Paid",
  released: "Released",
  cancelled: "Cancelled",
  refunded: "Refunded",
} as const;

export const SUBSCRIPTION_STATUS = {
  active: "Active",
  paused: "Paused",
  past_due: "Past due",
  cancelled: "Cancelled",
} as const;

export const FUNNEL_STAGE: Readonly<Record<FunnelStage, string>> = {
  labs: "Labs",
  questions: "Questions",
  reviewing: "Reviewing",
  checkout: "Checkout",
  paid: "Paid",
};

export const DECLINE_REASON: Readonly<Record<DeclineReason, string>> = {
  incomplete_information: "Incomplete information",
  contraindication: "Contraindication",
  not_appropriate: "Not appropriate",
  other: "Other",
};

export const EDIT_REASON: Readonly<Record<ReviewEditReason, string>> = {
  contraindication: "Contraindication",
  unnecessary: "Unnecessary",
  dose: "Dose",
  missing_context: "Missing context",
  manufacturing: "Manufacturing",
  patient_preference: "Patient preference",
  cost: "Cost",
  other: "Other",
};

export const EDIT_CHANGE: Readonly<Record<ReviewEditChange, string>> = {
  added: "Add",
  removed: "Remove",
  dose_changed: "Change dose",
};

export const DIET: Readonly<Record<NonNullable<IntakeAnswers["diet"]>, string>> = {
  omnivore: "Omnivore",
  pescatarian: "Pescatarian",
  vegetarian: "Vegetarian",
  vegan: "Vegan",
  other: "Other",
};

export const GOAL: Readonly<Record<NonNullable<IntakeAnswers["goal"]>, string>> = {
  general: "General",
  energy: "Energy",
  strength: "Strength",
  weight: "Weight",
  other: "Other",
};

export const EXERCISE: Readonly<Record<NonNullable<IntakeAnswers["exercise"]>["type"], string>> = {
  none: "None",
  strength: "Strength",
  endurance: "Endurance",
  mixed: "Mixed",
  other: "Other",
};

const ACTIONS = new Map([
  ["user_locked", "Locked"],
  ["user_unlocked", "Unlocked"],
  ["patient_read", "Record opened"],
  ["patient_link_moved", "Link moved"],
  ["patient_link_removed", "Link removed"],
  ["clinical_review_opened", "Review opened"],
  ["clinical_review_approved", "Review approved"],
  ["clinical_review_declined", "Review declined"],
  ["extraction_retried", "Extraction retried"],
  ["reaction_resolved", "Reaction resolved"],
  ["pharmacy_status_set", "Status updated"],
  ["pharmacy_resubmitted", "Resubmitted"],
  ["pharmacy_cancel_requested", "Cancel requested"],
  ["pharmacy_cancelled", "Cancelled"],
  ["pharmacy_entered", "Entered at pharmacy"],
  ["subscription_paused", "Paused"],
  ["subscription_cancelled", "Subscription cancelled"],
  ["order_refunded", "Refunded"],
  ["licence_verified", "Licence verified"],
  ["ingredient_created", "Ingredient added"],
  ["ingredient_updated", "Ingredient updated"],
  ["rule_set_saved", "Rule set saved"],
  ["engine_promoted", "Promoted to live"],
  ["engine_rolled_back", "Rolled back"],
  ["evaluation_case_rated", "Case rated"],
  ["evaluation_case_added", "Case added"],
]);

export function action(code: string): string {
  return ACTIONS.get(code) ?? code.replaceAll("_", " ");
}

const TARGETS = new Map([
  ["user", "Account"],
  ["patient", "Patient record"],
  ["patient_organization", "Clinic link"],
]);

export function targetType(code: string): string {
  return TARGETS.get(code) ?? code.replaceAll("_", " ");
}

const REFUSALS = new Map([
  ["reason_required", "Give a reason first."],
  ["forbidden", "Your role cannot do this."],
  ["cannot_lock_self", "You cannot lock your own account."],
  ["already_locked", "This account is already locked. Reload to see who locked it."],
  ["not_locked", "This account is already unlocked. Reload to see the latest."],
  ["link_removed", "This link was already removed."],
  ["same_organization", "The link is already at that clinic."],
  ["unknown_organization", "Pick a clinic from the list."],
  ["already_linked", "The patient already has a link to that clinic."],
  ["already_verified", "This licence is already verified. Reload to see who verified it."],
  ["passkey_unsupported", "This browser cannot use a passkey."],
  ["passkey_refused", "Passkeys are for staff and clinic accounts."],
  ["passkey_cancelled", "Passkey sign-in was cancelled."],
  ["passkey_failed", "The passkey could not be used. Try the email link."],
  ["already_resolved", "This report is already resolved. Reload to see the latest."],
  ["not_failed", "This file is not waiting to be read again."],
  ["not_found", "Nothing was found at this address."],
  ["signed_out", "Your session ended. Sign in again."],
  ["access_required", "Sign in through Cloudflare Access first."],
  ["too_many_requests", "Too many sign-in links for this email in the last hour. Try again later."],
  ["invalid_email", "Enter a full email address."],
  ["offline", "The server could not be reached. Check your connection and try again."],
  ["note_required", "Write a note for the customer."],
  [
    "invalid_edit",
    "Each edit needs an ingredient, a change, a reason and a dose when the dose changes.",
  ],
  ["illegal_transition", "This review cannot move to that state."],
  ["already_decided", "This review was already decided. Reload to see the latest."],
  ["constraint_failed", "This formula fails a safety check. Change the edits or decline."],
  ["unknown_state", "That is not a pharmacy order state."],
  ["state_required", "Pick the status to record."],
  ["cannot_resubmit", "Only a queued or rejected order can be sent again."],
  ["already_cancelled", "This is already cancelled. Reload to see the latest."],
  ["cannot_cancel", "This order cannot be cancelled from its current status."],
  ["cancel_failed", "The pharmacy could not take the cancellation."],
  ["no_open_task", "There is no open pharmacy task on this order."],
  ["missing_external_id", "Enter the pharmacy portal's reference first."],
  ["months_required", "Pick 1, 2 or 3 months."],
  ["already_paused", "This subscription is already paused. Reload to see until when."],
  ["cannot_pause", "This subscription cannot be paused from its current status."],
  ["cannot_refund", "This charge cannot be refunded from its current status."],
]);

export function refusal(code: string): string {
  return REFUSALS.get(code) ?? "Something went wrong on the server. Try again.";
}
